import * as React from "react";
import { map, size } from "lodash";
import { Waypoint } from "react-waypoint";
import styled from "styled-components";
import { content, transparentize } from "@glossy";
import { Locale } from "@linkfluence/models/locale";
import { sanitizeHTML } from "@tootools/dom";
import Link from "@ui-kit/utils/link";
import Empty from "@radarly/components/navigation/switcher/empty";
import Loading from "@radarly/components/navigation/switcher/loading";
import ProjectPortrait from "@radarly/components/portrait/project";
import TooltipProject from "@radarly/components/tooltip/project";
import { useCurrentUserTimezone } from "@radarly/core/user/use-cases";
import { tr } from "@radarly/i18n";
import { SearchableWorkspace } from "@radarly/models/workspace";
import { IRouterService } from "@radarly/shared/router";
import { Page } from "@radarly/shared/router/pages";

const WorkspaceCard = styled(Link)`
  color: ${content};
  display: block;
  width: 100%;
  font-size: 14px;
  height: 60px;
  line-height: 60px;
  padding: 0 16px;
  &:not(:last-child) {
    border-bottom: solid 1px ${(props) => content(props, "tertiary")};
  }
  &:hover {
    background-color: ${(props) => transparentize(0.85, content(props))};
    text-decoration: none;
  }
`;

const Portrait = styled(ProjectPortrait)`
  margin-right: 8px;
`;

const Arrow = styled.span`
  &:after {
    content: "›";
    width: 14px;
    display: inline-block;
    text-align: center;
    font-size: 16px;
    line-height: 1.1em;
  }
`;

const Label = styled.span`
  em {
    font-weight: bold;
  }
`;

interface IProps {
  workspaces: SearchableWorkspace[];
  locale: Locale;
  canFetchMore: boolean;
  loading?: boolean;
  fetchMore: () => void;
  onClickWorkspace?: (workspaceId: number) => void;
  router: IRouterService;
}

const NavigationSwitcherSearchWorkspaces = ({
  workspaces,
  locale,
  canFetchMore = false,
  loading,
  fetchMore,
  onClickWorkspace,
  router,
}: IProps) => {
  const timezone = useCurrentUserTimezone();
  const sizeWorkspaces = size(workspaces);
  return (
    <>
      {loading && !sizeWorkspaces ? (
        <Loading />
      ) : sizeWorkspaces ? (
        map(workspaces, (workspace, i) => (
          <TooltipProject
            key={i}
            locale={locale}
            isAccessible={workspace.project.isAccessible}
            isActive={workspace.project.isActive}
            placement="left"
          >
            <WorkspaceCard
              url={
                workspace.project.isAccessible
                  ? router.getWorkspaceUrlFromPage(
                      Page.workspaceHome,
                      workspace.project,
                      workspace.id,
                      { timezone }
                    )
                  : ""
              }
              onClick={() => onClickWorkspace(workspace.id)}
              key={workspace.id}
            >
              <Label
                as={Label}
                dangerouslySetInnerHTML={sanitizeHTML(
                  workspace.highlight["project.label"] ||
                    workspace.project.label
                )}
              />
              <Arrow />
              <Portrait
                picture={workspace.imageUrl}
                isAccessible={workspace.project.isAccessible}
                isActive={workspace.project.isActive}
                size={24}
              />
              <Label
                as={Label}
                dangerouslySetInnerHTML={sanitizeHTML(
                  workspace.highlight["dashboard.label"] || workspace.label
                )}
              />
            </WorkspaceCard>
          </TooltipProject>
        ))
      ) : (
        <Empty label={tr(locale, "SWITCHER::SEARCH_DASHBOARDS_EMPTY")} />
      )}
      {canFetchMore && !loading && <Waypoint onEnter={() => fetchMore()} />}
    </>
  );
};

export default NavigationSwitcherSearchWorkspaces;
